/**
 * Einen Kunden löschen — mit allem, was daran hängt.
 *
 * Beim Einführen entstehen Kunden, die es nicht geben sollte: zum
 * Ausprobieren angelegt, mit Plätzen, Prüfungen und abgeschlossenen
 * Berichten daran. Die müssen weg, sonst stehen sie für immer in der Liste
 * und verfälschen jede Zahl.
 *
 * Das zu verbieten wäre bequem und falsch — dann bliebe nur der Griff in
 * die Datenbank, und der ist gefährlicher als ein Knopf mit einer
 * Rückfrage. Also geht es, aber nicht nebenbei:
 *
 *   - Vorher steht da, was verschwindet. Einer Kundenzeile sieht man nicht
 *     an, dass drei Berichte daran hängen.
 *   - Die Nummern werden aufgezählt. Eine Berichts- oder Rechnungsnummer,
 *     die danach fehlt, ist eine Lücke in einer Reihe, die lückenlos sein
 *     soll.
 *   - Der Name muss abgeschrieben werden. Wer das tut, hat gelesen, was
 *     daneben steht; bei „Wirklich löschen?" ist das nicht so.
 */

import { useState } from 'react'
import { useKundeLoeschen, useLoeschvorschau } from '../api/abfragen'
import { Blatt } from './Blatt'
import { Fehlerhinweis, Ladeanzeige } from './Hinweise'

export function KundeLoeschen({
  kundeId,
  name,
  schliessen,
  geloescht,
}: {
  kundeId: string
  name: string
  schliessen: () => void
  geloescht: () => void
}) {
  const vorschau = useLoeschvorschau(kundeId, true)
  const loeschen = useKundeLoeschen()
  const [wort, setzeWort] = useState('')

  const v = vorschau.data

  return (
    <Blatt
      titel="Kunde löschen"
      untertitel={name}
      schliessen={schliessen}
      fuss={
        <div className="flex gap-3">
          <button type="button" className="knopf-still flex-1" onClick={schliessen}>
            Abbrechen
          </button>
          <button
            type="button"
            className="knopf-warnung flex-1"
            disabled={wort.trim() !== name || loeschen.isPending || !v}
            onClick={() =>
              loeschen.mutate(
                { id: kundeId, bestaetigung: wort.trim() },
                { onSuccess: geloescht },
              )
            }
          >
            {loeschen.isPending ? 'Löscht …' : 'Endgültig löschen'}
          </button>
        </div>
      }
    >
      {vorschau.isLoading && <Ladeanzeige />}
      <Fehlerhinweis fehler={vorschau.error} />

      {v && (
        <div className="space-y-3">
          {v.ist_beispiel && (
            <p className="rounded-feld border-2 border-marke-200 bg-marke-50 p-3 text-marke-700">
              Das ist der Beispielkunde. Er zählt in keinem Nummernkreis mit —
              ihn zu löschen kostet nichts.
            </p>
          )}

          <div className="rounded-feld border-2 border-stufe-3 bg-stufe-3-grund p-3">
            <p className="font-semibold text-stufe-3">Das verschwindet mit:</p>
            <ul className="mt-1 list-disc pl-5">
              <li>
                {v.spielplaetze} {v.spielplaetze === 1 ? 'Spielplatz' : 'Spielplätze'}{' '}
                mit {v.geraete} {v.geraete === 1 ? 'Gerät' : 'Geräten'}
              </li>
              <li>
                {v.pruefungen} {v.pruefungen === 1 ? 'Prüfung' : 'Prüfungen'}
                {v.abgeschlossene_pruefungen > 0 && (
                  <>
                    , davon <strong>{v.abgeschlossene_pruefungen} abgeschlossen</strong>
                  </>
                )}
              </li>
              <li>
                {v.fotos} {v.fotos === 1 ? 'Foto' : 'Fotos'}, {v.dokumente}{' '}
                {v.dokumente === 1 ? 'Dokument' : 'Dokumente'}, {v.portalzugaenge}{' '}
                {v.portalzugaenge === 1 ? 'Portalzugang' : 'Portalzugänge'}
              </li>
              {v.belege > 0 && (
                <li>
                  <strong>
                    {v.belege} {v.belege === 1 ? 'Beleg' : 'Belege'}
                  </strong>{' '}
                  (Angebote, Auftragsbestätigungen, Rechnungen)
                </li>
              )}
            </ul>
          </div>

          {/* Die Nummern ausdrücklich, nicht nur die Anzahl: was fehlt,
              soll man beim Namen kennen, wenn später jemand danach fragt. */}
          {(v.berichtsnummern.length > 0 || v.belegnummern.length > 0) && (
            <div className="rounded-feld border-2 border-stufe-2 bg-stufe-2-grund p-3">
              <p className="font-semibold text-stufe-2">
                Diese Nummern fehlen danach in ihrer Reihe:
              </p>
              {v.berichtsnummern.length > 0 && (
                <p className="mt-1">
                  <span className="text-slate-700">Berichte: </span>
                  <span className="font-mono">{v.berichtsnummern.join(', ')}</span>
                </p>
              )}
              {v.belegnummern.length > 0 && (
                <p className="mt-1">
                  <span className="text-slate-700">Belege: </span>
                  <span className="font-mono">{v.belegnummern.join(', ')}</span>
                </p>
              )}
              <p className="mt-2 text-base">
                Rechnungsnummern unterliegen der Aufbewahrungspflicht. Löschen Sie
                hier nur, was tatsächlich zum Ausprobieren entstanden ist.
              </p>
            </div>
          )}

          <div>
            <label htmlFor="k-wort">
              Zum Bestätigen den Namen eingeben: <strong>{name}</strong>
            </label>
            <input
              id="k-wort"
              value={wort}
              onChange={(e) => setzeWort(e.target.value)}
              autoComplete="off"
              spellCheck={false}
            />
          </div>

          <Fehlerhinweis fehler={loeschen.error} />
        </div>
      )}
    </Blatt>
  )
}
